import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandsManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    logo_url: '',
    brand_type: 'Rappresentanza',
    description: '',
    website: ''
  });

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Errore nel caricamento dei marchi');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Il nome del marchio è obbligatorio');
      return;
    }

    try {
      if (editingBrand) {
        await axios.put(
          `${API}/admin/brands/${editingBrand.brand_id}`,
          formData,
          { withCredentials: true }
        );
        toast.success('Marchio aggiornato con successo!');
      } else {
        await axios.post(
          `${API}/admin/brands`,
          formData,
          { withCredentials: true }
        );
        toast.success('Marchio creato con successo!');
      }
      
      setIsDialogOpen(false);
      setEditingBrand(null);
      setFormData({
        name: '',
        logo_url: '',
        brand_type: 'Rappresentanza',
        description: '',
        website: ''
      });
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      toast.error(editingBrand ? 'Errore nell\'aggiornamento' : 'Errore nella creazione');
    }
  };

  const handleDelete = async (brandId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo marchio? I prodotti associati non saranno eliminati.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, {
        withCredentials: true
      });
      toast.success('Marchio eliminato con successo!');
      fetchBrands();
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Errore nell\'eliminazione del marchio');
    }
  };

  const openDialog = (brand = null) => {
    if (brand) {
      setEditingBrand(brand);
      setFormData({
        name: brand.name,
        logo_url: brand.logo_url || '',
        brand_type: brand.brand_type,
        description: brand.description || '',
        website: brand.website || ''
      });
    } else {
      setEditingBrand(null);
      setFormData({
        name: '',
        logo_url: '',
        brand_type: 'Rappresentanza',
        description: '',
        website: ''
      });
    }
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingBrand(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-600">Caricamento marchi...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
            <Building2 className="text-orange-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Marchi e Aziende Partner</h2>
            <p className="text-sm text-slate-600">Gestisci i loghi e le informazioni dei marchi</p>
          </div>
        </div>
        <Button
          onClick={() => openDialog()}
          className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
        >
          <Plus size={18} />
          Nuovo Marchio
        </Button>
      </div>

      {/* Brands Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {brands.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center">
              <Building2 className="mx-auto mb-4 text-slate-400" size={48} />
              <p className="text-slate-600 mb-4">Nessun marchio presente</p>
              <Button onClick={() => openDialog()} variant="outline">
                <Plus size={16} className="mr-2" />
                Aggiungi il primo marchio
              </Button>
            </CardContent>
          </Card>
        ) : (
          brands.map((brand) => (
            <Card key={brand.brand_id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{brand.name}</CardTitle>
                    <span className={`inline-block mt-2 px-2 py-1 text-xs rounded-full ${
                      brand.brand_type === 'Rappresentanza' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {brand.brand_type}
                    </span>
                  </div>
                  {brand.logo_url && (
                    <img 
                      src={brand.logo_url} 
                      alt={brand.name}
                      className="w-16 h-16 object-contain ml-2 rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {brand.description && (
                  <p className="text-sm text-slate-600 line-clamp-2">{brand.description}</p>
                )}
                {brand.website && (
                  <a 
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-orange-600 hover:underline block truncate"
                  >
                    {brand.website}
                  </a>
                )}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDialog(brand)}
                    className="flex-1"
                  >
                    <Edit size={14} className="mr-1" />
                    Modifica
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(brand.brand_id)}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingBrand ? 'Modifica Marchio' : 'Nuovo Marchio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nome Marchio *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: Weber, Mapei, Sikkens..."
                required
              />
            </div>

            <div>
              <Label htmlFor="brand_type">Tipo *</Label>
              <Select
                value={formData.brand_type}
                onValueChange={(value) => setFormData({ ...formData, brand_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rappresentanza">Rappresentanza</SelectItem>
                  <SelectItem value="Distribuzione">Distribuzione</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Rappresentanza = partner ufficiale | Distribuzione = rivenditore
              </p>
            </div>

            <div>
              <Label htmlFor="logo_url">URL Logo</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://esempio.com/logo.png"
              />
              <p className="text-xs text-slate-500 mt-1">
                Inserisci l'URL del logo (formato PNG, JPG, SVG)
              </p>
              {formData.logo_url && (
                <div className="mt-2 p-2 border rounded">
                  <p className="text-xs text-slate-600 mb-2">Anteprima:</p>
                  <img 
                    src={formData.logo_url} 
                    alt="Logo preview"
                    className="h-16 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      toast.error('URL logo non valido');
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="website">Sito Web</Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.esempio.com"
              />
            </div>

            <div>
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrizione del marchio..."
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={closeDialog}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                {editingBrand ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandsManagement;
